import type { ChartStakeholder, ProcessedReport } from '@integratio/core';
import { useBranding } from '../branding/BrandingProvider';
import { formatDate, formatDateTime, formatPercent } from '../lib/format';
import { ContextEnvironmentChart } from './charts/ContextEnvironmentChart';
import { PriorityChart } from './charts/PriorityChart';
import { BehaviorChart } from './charts/BehaviorChart';
import { PowerInterestMatrix } from './charts/PowerInterestMatrix';
import { IsoLegend } from './charts/IsoLegend';
import { chunkRows, paginateGroups, type DetailPage } from './print/paginate';

/**
 * Linhas de tabela por página (2 colunas), calibrado com margem de segurança.
 * A coluna "Categoria" frequentemente quebra em 2-3 linhas, então cada linha
 * de tabela ocupa mais espaço vertical do que uma linha simples; ~9-10 linhas
 * por coluna é o limite real observado antes do corte pelo overflow:hidden.
 */
const TABLE_ROWS_PER_PAGE = 16;

export type PrintChart = 'contexto' | 'prioridade' | 'postura' | 'poder-interesses' | 'tabela';

interface SheetProps {
  report: ProcessedReport;
  eyebrow: string;
  title: string;
  subtitle?: string;
  page: number;
  totalPages: number;
  children: React.ReactNode;
}

function Sheet({ report, eyebrow, title, subtitle, page, totalPages, children }: SheetProps): JSX.Element {
  const branding = useBranding();
  return (
    <section className="print-sheet flex flex-col px-[14mm] py-[10mm]">
      <header className="flex items-start justify-between gap-6 border-b border-brand-line pb-3">
        <div>
          <p className="eyebrow">{eyebrow}</p>
          <h2 className="mt-1 text-[22px] font-semibold leading-tight tracking-tight text-brand-ink">{title}</h2>
          {subtitle && <p className="mt-1 max-w-[190mm] text-[11px] text-brand-slate">{subtitle}</p>}
        </div>
        <div className="text-right">
          <img src={branding.logoUrl} alt={branding.appName} className="ml-auto h-8 w-auto" />
          <p className="mt-1 text-[9px] uppercase tracking-wider text-brand-slate">
            Gerado em {formatDateTime(report.generatedAt)}
          </p>
        </div>
      </header>

      <div className="flex-1 py-4">{children}</div>

      <footer className="flex items-center justify-between gap-4 border-t border-brand-line pt-2 text-[9px] text-brand-slate">
        <span>{branding.footer}</span>
        <span className="truncate">Fonte: {report.fileName}</span>
        <span>
          Página {page} de {totalPages}
        </span>
      </footer>
    </section>
  );
}

function CoverSheet({ report }: { report: ProcessedReport }): JSX.Element {
  const branding = useBranding();
  const topContext = [...report.contextGroups].sort((a, b) => b.count - a.count)[0];
  const highPriority = report.priorityGroups.find((group) => group.level === 'ALTA');

  return (
    <section className="print-sheet relative flex flex-col justify-between px-[20mm] py-[16mm]">
      <span aria-hidden className="absolute left-0 top-0 h-2 w-full" style={{ backgroundColor: branding.colors.primary }} />
      <div>
        <img src={branding.logoUrl} alt={branding.appName} className="h-12 w-auto" />
        <p className="eyebrow mt-12">Relatório de Análise de Stakeholders</p>
        <h1 className="mt-3 max-w-[200mm] text-[42px] font-semibold leading-[1.05] tracking-tight text-brand-ink">
          Mapeamento e Priorização de Stakeholders
        </h1>
        <p className="mt-4 max-w-[170mm] text-sm text-brand-slate">
          Documento gerado automaticamente a partir da planilha de stakeholders informada, com agrupamentos,
          percentuais e listas calculados pelo sistema.
        </p>
      </div>

      <div className="grid grid-cols-4 gap-6 border-t border-brand-line pt-6">
        <div>
          <p className="eyebrow">Total de stakeholders</p>
          <p className="mt-1 text-3xl font-semibold tabular-nums text-brand-ink">{report.totalStakeholders}</p>
        </div>
        <div>
          <p className="eyebrow">Contexto predominante</p>
          <p className="mt-1 text-lg font-semibold text-brand-ink">{topContext?.name ?? '—'}</p>
          {topContext && <p className="text-xs text-brand-slate">{formatPercent(topContext.percentage)}</p>}
        </div>
        <div>
          <p className="eyebrow">Prioridade alta</p>
          <p className="mt-1 text-lg font-semibold text-brand-ink">
            {highPriority ? formatPercent(highPriority.percentage) : '—'}
          </p>
          {highPriority && <p className="text-xs text-brand-slate">{highPriority.count} stakeholders</p>}
        </div>
        <div>
          <p className="eyebrow">Emissão</p>
          <p className="mt-1 text-lg font-semibold text-brand-ink">{formatDate(report.generatedAt)}</p>
          <p className="truncate text-xs text-brand-slate">{report.fileName}</p>
        </div>
      </div>
    </section>
  );
}

function SummarySheet({ report, totalPages }: { report: ProcessedReport; totalPages: number }): JSX.Element {
  const sections = [
    { title: 'Contexto do Ambiente', description: 'Classificação por posicionamento e poder, com percentuais e listas por grupo.' },
    { title: 'Prioridade de Atuação', description: 'Níveis de prioridade e ações recomendadas, conforme a planilha.' },
    { title: 'Postura e Comportamento', description: 'Matriz de influência e interesses (coluna Poder X Interesses).' },
    { title: 'Poder x Interesses', description: 'Dispersão dos valores numéricos de poder e interesses.' },
    { title: 'Tabela resumida', description: 'Relação alfabética dos stakeholders processados.' },
  ];

  return (
    <Sheet
      report={report}
      eyebrow="Introdução"
      title="Como este relatório foi construído"
      page={2}
      totalPages={totalPages}
    >
      <div className="grid h-full grid-cols-[1.1fr_1fr] gap-10">
        <div className="space-y-4 text-[12px] leading-relaxed text-brand-graphite">
          <p>
            Os dados apresentados foram importados da planilha <strong>{report.fileName}</strong> e processados sem
            qualquer alteração de conteúdo: nomes, categorias e classificações são exibidos exatamente como constam no
            arquivo de origem.
          </p>
          <p>
            Os percentuais de cada grupo são calculados sobre o total de{' '}
            <strong>{report.totalStakeholders} stakeholders</strong> válidos importados, pela razão entre a quantidade
            do grupo e esse total. Grupos previstos na metodologia que não possuem stakeholders são mantidos nos
            gráficos com a indicação “Não possui Stakeholder”.
          </p>
          <p>
            Os agrupamentos seguem o vocabulário da planilha: Contexto do Ambiente (Seguro, Favorável, Indiferente,
            Sensível, Ameaçador e Crítico), Postura e Comportamento (Ativismo, Colaboração, Aceitação e Resignação) e
            Prioridade (Alta, Média e Baixa, com as respectivas ações).
          </p>
          {report.warningsCount > 0 && (
            <p className="rounded-xl border border-state-attention/40 bg-state-attention/5 p-3 text-[11px] text-brand-graphite">
              A conferência registrou {report.warningsCount} alerta(s) de preenchimento na planilha de origem. Os
              registros foram processados como informados.
            </p>
          )}
        </div>

        <div>
          <p className="eyebrow">Sumário</p>
          <ol className="mt-3 space-y-2">
            {sections.map((section, index) => (
              <li key={section.title} className="flex gap-3 rounded-xl border border-brand-line p-3">
                <span className="text-[11px] font-bold tabular-nums text-brand-red">
                  {String(index + 3).padStart(2, '0')}
                </span>
                <span>
                  <span className="block text-[12px] font-semibold text-brand-ink">{section.title}</span>
                  <span className="block text-[10px] text-brand-slate">{section.description}</span>
                </span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </Sheet>
  );
}

function TableSheet({
  report,
  rows,
  page,
  totalPages,
  pageLabel,
}: {
  report: ProcessedReport;
  rows: ChartStakeholder[];
  page: number;
  totalPages: number;
  pageLabel?: string;
}): JSX.Element {
  const half = Math.ceil(rows.length / 2);
  const columns = [rows.slice(0, half), rows.slice(half)];

  return (
    <Sheet
      report={report}
      eyebrow="Anexo"
      title={`Tabela resumida de stakeholders${pageLabel ? ` · ${pageLabel}` : ''}`}
      subtitle="Relação alfabética dos stakeholders processados."
      page={page}
      totalPages={totalPages}
    >
      <div className="grid grid-cols-2 gap-6">
        {columns.map((column, index) => (
          <table key={index} className="w-full border-collapse text-[10px]">
            <thead>
              <tr className="bg-brand-mist text-left uppercase tracking-wider text-brand-slate">
                <th className="px-2 py-1.5 font-semibold">Nº</th>
                <th className="px-2 py-1.5 font-semibold">Stakeholder</th>
                <th className="px-2 py-1.5 font-semibold">Cargo</th>
                <th className="px-2 py-1.5 font-semibold">Empresa/Instituição</th>
                <th className="px-2 py-1.5 font-semibold">Categoria</th>
              </tr>
            </thead>
            <tbody>
              {column.map((item) => (
                <tr key={item.id} className="avoid-break border-t border-brand-line/70">
                  <td className="px-2 py-1 tabular-nums text-brand-slate">{item.numero || '—'}</td>
                  <td className="px-2 py-1 font-medium text-brand-ink">{item.nome}</td>
                  <td className="px-2 py-1 text-brand-slate">{item.cargo || '—'}</td>
                  <td className="px-2 py-1 text-brand-slate">{item.empresaInstituicao || '—'}</td>
                  <td className="px-2 py-1 text-brand-slate">{item.categoria || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
      </div>
    </Sheet>
  );
}

/** Renderiza uma página de detalhamento (3 colunas) a partir de um DetailPage já paginado. */
function DetailSheet({
  report,
  eyebrow,
  title,
  page,
  totalPages,
  detailPage,
}: {
  report: ProcessedReport;
  eyebrow: string;
  title: string;
  page: number;
  totalPages: number;
  detailPage: DetailPage;
}): JSX.Element {
  return (
    <Sheet report={report} eyebrow={eyebrow} title={title} page={page} totalPages={totalPages}>
      <div className="grid grid-cols-3 gap-5">
        {detailPage.columns.map((groups, index) => (
          <IsoLegend key={index} groups={groups} total={report.totalStakeholders} limit={Infinity} />
        ))}
      </div>
    </Sheet>
  );
}

interface ChartSheetSpec {
  key: PrintChart;
  eyebrow: string;
  title: string;
  subtitle?: string;
  content: React.ReactNode;
  /** Páginas de detalhamento já paginadas dinamicamente (2 colunas cada). */
  detailPages: DetailPage[];
}

/** Pré-visualização paginada (A4 paisagem) usada na tela e pelo renderizador de PDF. */
export function FullReportPreview({ report, chart }: { report: ProcessedReport; chart?: PrintChart }): JSX.Element {
  // Flatten subgrupos de prioridade para detalhamento (mantendo referência ao nível pai)
  const priorityDetailGroups = report.priorityGroups.flatMap((lvl) =>
    lvl.subgroups.map((sg) => ({ ...sg, subtitle: lvl.name ?? lvl.level })),
  );

  const chartSheets: ChartSheetSpec[] = [
    {
      key: 'contexto',
      eyebrow: 'Análise de Stakeholders',
      title: 'Contexto do Ambiente',
      subtitle: 'Classificação por posicionamento e poder. A altura de cada bloco é proporcional ao percentual do grupo.',
      content: <ContextEnvironmentChart groups={report.contextGroups} total={report.totalStakeholders} print />,
      detailPages: paginateGroups(report.contextGroups),
    },
    {
      key: 'prioridade',
      eyebrow: 'Análise de Stakeholders',
      title: 'Prioridade de Atuação',
      subtitle: 'Distribuição por nível de prioridade e ação recomendada.',
      content: <PriorityChart groups={report.priorityGroups} total={report.totalStakeholders} print />,
      detailPages: paginateGroups(priorityDetailGroups),
    },
    {
      key: 'postura',
      eyebrow: 'Análise de Stakeholders',
      title: 'Postura e Comportamento',
      subtitle: 'Matriz de influência e interesses; o número sobre cada bloco é a quantidade de stakeholders.',
      content: <BehaviorChart groups={report.behaviorGroups} total={report.totalStakeholders} print />,
      detailPages: paginateGroups(report.behaviorGroups),
    },
    {
      key: 'poder-interesses',
      eyebrow: 'Análise de Stakeholders',
      title: 'Poder x Interesses',
      subtitle: 'Dispersão dos valores de poder e interesses informados na planilha.',
      content: <PowerInterestMatrix data={report.powerInterest} total={report.totalStakeholders} print />,
      detailPages: paginateGroups(
        report.powerInterest.unplotted.length > 0
          ? [
              ...report.powerInterest.quadrants,
              {
                key: 'unplotted',
                name: 'Sem valores numéricos',
                count: report.powerInterest.unplotted.length,
                percentage: 0,
                color: '#8E8C99',
                stakeholders: report.powerInterest.unplotted,
                emptyText: '',
              },
            ]
          : report.powerInterest.quadrants,
      ),
    },
  ];

  const tablePages = chunkRows(report.stakeholders, TABLE_ROWS_PER_PAGE);

  if (chart) {
    if (chart === 'tabela') {
      return (
        <div className="space-y-6">
          {tablePages.map((rows, index) => (
            <TableSheet
              key={index}
              report={report}
              rows={rows}
              page={index + 1}
              totalPages={tablePages.length}
              pageLabel={tablePages.length > 1 ? `Parte ${index + 1} de ${tablePages.length}` : undefined}
            />
          ))}
        </div>
      );
    }
    const sheet = chartSheets.find((item) => item.key === chart);
    if (!sheet) return <p className="p-10 text-sm text-brand-slate">Gráfico não encontrado.</p>;
    const totalPages = 1 + sheet.detailPages.length;
    return (
      <div className="space-y-6">
        <Sheet report={report} eyebrow={sheet.eyebrow} title={sheet.title} subtitle={sheet.subtitle} page={1} totalPages={totalPages}>
          {sheet.content}
        </Sheet>
        {sheet.detailPages.map((detailPage, index) => (
          <DetailSheet
            key={index}
            report={report}
            eyebrow={sheet.eyebrow}
            title={`${sheet.title} · Detalhamento por grupo${sheet.detailPages.length > 1 ? ` (${index + 1}/${sheet.detailPages.length})` : ''}`}
            page={index + 2}
            totalPages={totalPages}
            detailPage={detailPage}
          />
        ))}
      </div>
    );
  }

  const pagesForCharts = chartSheets.reduce((acc, item) => acc + 1 + item.detailPages.length, 0);
  const totalPages = 2 + pagesForCharts + tablePages.length;

  return (
    <div className="space-y-6 print:space-y-0">
      <CoverSheet report={report} />
      <SummarySheet report={report} totalPages={totalPages} />
      {(() => {
        const pages: JSX.Element[] = [];
        let pageNumber = 3;
        for (const sheet of chartSheets) {
          pages.push(
            <Sheet
              key={`${sheet.key}-chart`}
              report={report}
              eyebrow={sheet.eyebrow}
              title={sheet.title}
              subtitle={sheet.subtitle}
              page={pageNumber}
              totalPages={totalPages}
            >
              {sheet.content}
            </Sheet>,
          );
          pageNumber += 1;
          sheet.detailPages.forEach((detailPage, index) => {
            pages.push(
              <DetailSheet
                key={`${sheet.key}-detail-${index}`}
                report={report}
                eyebrow={sheet.eyebrow}
                title={`${sheet.title} · Detalhamento por grupo${sheet.detailPages.length > 1 ? ` (${index + 1}/${sheet.detailPages.length})` : ''}`}
                page={pageNumber}
                totalPages={totalPages}
                detailPage={detailPage}
              />,
            );
            pageNumber += 1;
          });
        }
        return pages;
      })()}
      {tablePages.map((rows, index) => (
        <TableSheet
          key={index}
          report={report}
          rows={rows}
          page={2 + pagesForCharts + index + 1}
          totalPages={totalPages}
          pageLabel={tablePages.length > 1 ? `Parte ${index + 1} de ${tablePages.length}` : undefined}
        />
      ))}
    </div>
  );
}
